import React, {useEffect, useRef, useState} from "react";
import {create} from "simple-drawing-board";
import {CaptureDancingGif} from "./gif";
import {TextScene} from "../../common/text";
import {renderStrokes} from "./strokeRenderer";
import {CountdownBadge} from "../../common/countdown";
import {drawnScene} from "../../preview/samples";

const RECORDING_VERSION = 1;
const CANVAS_W = 500;
const CANVAS_H = 500;
const LINE_SIZE = 10;

const parseRecording = (raw) => {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.events)) return parsed;
    } catch (e) {
        // not a recording — legacy bitmap dataURL or junk
    }
    return null;
};

export const DancelestrationsBackdrop = () => (
    <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(rgb(221 160 53 / 25%), rgb(200 148 58)), url(/dancelestrations-assets/backdrop.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: -1,
        pointerEvents: 'none',
    }}/>
);

export const Dancelestrations = ({isControl, round, showPair = false, roundEnd, clockDrift}) => {
    const [task, setTask] = useState();

    const updateTask = () => {
        fetch('/dancelestrations/get-task?' + new URLSearchParams({round}), {credentials: 'include'})
            .then(response => response.json())
            .then(setTask)
    }

    useEffect(() => {
        if(isControl) return;
        updateTask();
    }, [round]);

    if(isControl) return <DancelestrationsAdmin isControl={isControl} round={round} showPair={showPair} />;

    if(!task) return null;
    if(task.task === 'draw') return <Draw word={task.word} saveDataType={task.save_data_type} existing={task.existing} roundEnd={roundEnd} clockDrift={clockDrift} />;
    if(task.task === 'guess-drawing') return <GuessDrawing drawing={task.drawing} saveDataType={task.save_data_type} existing={task.existing} updateTask={updateTask} roundEnd={roundEnd} clockDrift={clockDrift} />;
    if(task.task === 'take-dance-gif' && showPair) return <Pair pair={task.pair} />;
    if(task.task === 'take-dance-gif') return <CaptureDancingGif pair={task.pair} guess={task.your_guess} existing={task.existing} updateTask={updateTask} />;
    if(task.task === 'guess-dance') return <GuessDance danceGifUrl={task.dance_gif_url} saveDataType={task.save_data_type} existing={task.existing} updateTask={updateTask} roundEnd={roundEnd} clockDrift={clockDrift} />;

    return <div></div>;
}

const DancelestrationsAdmin = ({isControl, round, showPair}) => {
    let scene;

    switch (round) {
        case 0:
            scene = <TextScene isControl={isControl} text={<span style={{fontWeight: 'bold', fontSize: '180px'}}>draw!</span>} />;
            break;
        case 1:
            scene = <TextScene isControl={isControl} text={<span style={{fontWeight: 'bold', fontSize: '180px'}}>guess!</span>} />;
            break;
        case 2:
            scene = <TextScene isControl={isControl} text={<span style={{fontWeight: 'bold', fontSize: '180px'}}>{showPair ? `find!` : `create!`}</span>} />;
            break;
        case 3:
            scene = <TextScene isControl={isControl} text={<span style={{fontWeight: 'bold', fontSize: '180px'}}>guess!</span>} />;
            break;
        case 4:
            scene = <TextScene isControl={isControl} text={<span style={{fontWeight: 'bold', fontSize: '180px'}}>draw!</span>} />;
            break;
    }

    return scene;
}

const GuessDance = ({danceGifUrl, saveDataType, existing, updateTask, roundEnd, clockDrift}) => {
    const [guess, setGuess] = useState('');


    const submitAnswer = () => {
        if (!guess.trim()) return;
        fetch('/dancelestrations/save-data', {
            method: 'post',
            credentials: 'include',
            body: JSON.stringify({dataType: saveDataType, value: guess})
        }).then(updateTask)
    }

    const cardStyle = {
        border: '3px solid black',
        borderRadius: 6,
        backgroundColor: '#ffffff',
        color: 'black',
        boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
    };

    const headline = danceGifUrl ? 'What is it?' : 'Guess Anyway?';

    return (
        <div style={{
            alignSelf: 'flex-start',
            width: '100%',
            minHeight: '100dvh',
            boxSizing: 'border-box',
            padding: 'calc(16px + env(safe-area-inset-top)) 16px calc(24px + env(safe-area-inset-bottom))',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 12,
            color: 'white',
        }}>
            <DancelestrationsBackdrop />
            <CountdownBadge roundEnd={roundEnd} clockDrift={clockDrift}/>
            <div style={{
                ...cardStyle,
                alignSelf: 'center',
                marginTop: 80,
                textAlign: 'center',
                padding: '10px 24px 14px',
                lineHeight: 1.15,
            }}>
                <div style={{
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    opacity: 0.6,
                    marginBottom: 4,
                }}>
                    Guess
                </div>
                <div style={{fontSize: 36, fontWeight: 700}}>
                    {headline}
                </div>
            </div>

            {danceGifUrl ? (
                <img
                    src={danceGifUrl}
                    alt="dance"
                    style={{
                        display: 'block',
                        margin: '0 auto',
                        width: '100%',
                        height: 'auto',
                        backgroundColor: '#0a0a0a',
                        border: '3px solid black',
                        borderRadius: 6,
                        boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
                    }}
                />
            ) : (
                <div style={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    backgroundColor: '#0a0a0a',
                    border: '3px solid black',
                    borderRadius: 6,
                    boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 28,
                    textAlign: 'center',
                    padding: 16,
                    boxSizing: 'border-box',
                }}>
                    No Dance :(
                </div>
            )}

            {existing ? (
                <div style={{
                    ...cardStyle,
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontSize: 24,
                    fontWeight: 600,
                }}>
                    You guessed: <strong>{existing}</strong>
                </div>
            ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                    <input
                        type="text"
                        className="inputbox"
                        placeholder="Answer"
                        value={guess}
                        onChange={(event) => setGuess(event.target.value)}
                        style={{
                            ...cardStyle,
                            padding: '14px 16px',
                            fontSize: 24,
                            fontWeight: 600,
                            fontFamily: 'inherit',
                            outline: 'none',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    />
                    <button
                        onClick={submitAnswer}
                        style={{
                            ...cardStyle,
                            padding: '14px 0',
                            fontSize: 22,
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            opacity: guess.trim() ? 1 : 0.55,
                        }}
                    >
                        Submit
                    </button>
                </div>
            )}
        </div>
    );
}

const Pair = ({pair}) => {

    const cardStyle = {
        border: '3px solid black',
        borderRadius: 6,
        backgroundColor: '#ffffff',
        color: 'black',
        boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
    };

    return (
        <div style={{
            alignSelf: 'flex-start',
            minHeight: '100dvh',
            boxSizing: 'border-box',
            padding: 'calc(16px + env(safe-area-inset-top)) 16px calc(24px + env(safe-area-inset-bottom))',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            color: 'white',
        }}>
            <DancelestrationsBackdrop />
            <div style={{
                ...cardStyle,
                alignSelf: 'center',
                textAlign: 'center',
                padding: '10px 24px 14px',
                lineHeight: 1.15,
            }}>
                <div style={{
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    opacity: 0.6,
                    marginBottom: 4,
                }}>
                    Find
                </div>
                <div style={{fontSize: 36, fontWeight: 700}}>
                    Your Pair
                </div>
            </div>

            <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <div style={{
                    width: '100%',
                    padding: 12,
                    textAlign: 'center',
                    border: '3px solid black',
                    borderRadius: 6,
                    backgroundColor: '#0a0a0a',
                    color: 'white',
                    boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
                }}>
                    {pair.image_url && (
                        <img
                            src={pair.image_url}
                            alt={pair.name}
                            style={{
                                width: '100%',
                                display: 'block',
                                borderRadius: 4,
                                marginBottom: 12,
                            }}
                        />
                    )}
                    <div style={{fontSize: 32, fontWeight: 700}}>
                        {pair.name}
                    </div>
                    {pair.nickname && (
                        <div style={{
                            fontSize: 20,
                            fontStyle: 'italic',
                            opacity: 0.75,
                            marginTop: 4,
                            marginBottom: 4,
                        }}>
                            {pair.nickname}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const GuessDrawing = ({drawing, saveDataType, existing, updateTask, roundEnd, clockDrift}) => {
    const [guess, setGuess] = useState('');


    useEffect(() => {
        const canvas = document.getElementById("drawing");
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const recording = parseRecording(drawing);
        if (recording) {
            renderStrokes(canvas, recording, {animated: false});
        }
    }, [drawing]);

    const submitAnswer = () => {
        if (!guess.trim()) return;
        fetch('/dancelestrations/save-data', {
            method: 'post',
            credentials: 'include',
            body: JSON.stringify({dataType: saveDataType, value: guess})
        }).then(updateTask)
    }

    const cardStyle = {
        border: '3px solid black',
        borderRadius: 6,
        backgroundColor: '#ffffff',
        color: 'black',
        boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
    };

    return (
        <div style={{
            alignSelf: 'flex-start',
            minHeight: '100dvh',
            boxSizing: 'border-box',
            padding: 'calc(16px + env(safe-area-inset-top)) 16px calc(24px + env(safe-area-inset-bottom))',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 12,
            color: 'white',
        }}>
            <DancelestrationsBackdrop />
            <CountdownBadge roundEnd={roundEnd} clockDrift={clockDrift}/>
            <div style={{
                ...cardStyle,
                alignSelf: 'center',
                marginTop: 80,
                textAlign: 'center',
                padding: '10px 24px 14px',
                lineHeight: 1.15,
            }}>
                <div style={{
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    opacity: 0.6,
                    marginBottom: 4,
                }}>
                    Guess
                </div>
                <div style={{fontSize: 36, fontWeight: 700}}>
                    What is it?
                </div>
            </div>

            <canvas
                id="drawing"
                width="500"
                height="500"
                style={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    height: 'auto',
                    backgroundColor: '#0a0a0a',
                    border: '3px solid black',
                    borderRadius: 6,
                    boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
                }}
            />

            {existing ? (
                <div style={{
                    ...cardStyle,
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontSize: 24,
                    fontWeight: 600,
                }}>
                    You guessed: <strong>{existing}</strong>
                </div>
            ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                    <input
                        type="text"
                        className="inputbox"
                        placeholder="Answer"
                        value={guess}
                        onChange={(event) => setGuess(event.target.value)}
                        style={{
                            ...cardStyle,
                            padding: '14px 16px',
                            fontSize: 24,
                            fontWeight: 600,
                            fontFamily: 'inherit',
                            outline: 'none',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    />
                    <button
                        onClick={submitAnswer}
                        style={{
                            ...cardStyle,
                            padding: '14px 0',
                            fontSize: 22,
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            opacity: guess.trim() ? 1 : 0.55,
                        }}
                    >
                        Submit
                    </button>
                </div>
            )}
        </div>
    );
}

const COLORS = ['#FFFFFF', '#EE204D', '#FFDC00', '#1F75FE', '#B5674D', '#FF7538', '#1CAC78', '#925EAE'];

const Draw = ({word, saveDataType, existing, roundEnd, clockDrift}) => {
    const [sdb, setSdb] = useState();
    const [selectedColor, setSelectedColor] = useState('#FFFFFF');

    // Refs read by SDB observer callbacks (which are bound once) so they
    // always see the latest values without re-binding handlers.
    const colorRef = useRef('#FFFFFF');
    const startTimeRef = useRef(null);
    const eventsRef = useRef([]);
    const currentStrokeRef = useRef(null);


    useEffect(() => {
        colorRef.current = selectedColor;
    }, [selectedColor]);

    useEffect(() => {
        const canvas = document.getElementById("drawing");
        const sdb = create(canvas);
        sdb.setLineSize(LINE_SIZE);
        sdb.setLineColor('white');

        // Reset recording state for this draw session.
        startTimeRef.current = null;
        eventsRef.current = [];
        currentStrokeRef.current = null;

        // Render any existing recording (e.g. partial draw on remount).
        const existingRecording = parseRecording(existing);
        if (existingRecording) {
            renderStrokes(canvas, existingRecording, {animated: false});
            // Seed the recording so the next strokes append to existing work.
            eventsRef.current = existingRecording.events.slice();
            // Pretend the recording started lastTs ms ago so new events
            // continue chronologically instead of resetting to ts=0.
            const last = eventsRef.current[eventsRef.current.length - 1];
            if (last) startTimeRef.current = performance.now() - (last.ts || 0);
        }

        const tsNow = () => {
            if (startTimeRef.current === null) startTimeRef.current = performance.now();
            return Math.round(performance.now() - startTimeRef.current);
        };

        const onBegin = ({x, y}) => {
            currentStrokeRef.current = {
                t: 'stroke',
                ts: tsNow(),
                c: colorRef.current,
                s: LINE_SIZE,
                pts: [[Math.round(x), Math.round(y)]],
            };
        };
        const onDraw = ({x, y}) => {
            if (currentStrokeRef.current) {
                currentStrokeRef.current.pts.push([Math.round(x), Math.round(y)]);
            }
        };
        const onEnd = () => {
            if (currentStrokeRef.current) {
                eventsRef.current.push(currentStrokeRef.current);
                currentStrokeRef.current = null;
            }
        };

        sdb.observer.on('drawBegin', onBegin);
        sdb.observer.on('draw', onDraw);
        sdb.observer.on('drawEnd', onEnd);

        setSdb(sdb);

        return () => {
            sdb.observer.off('drawBegin', onBegin);
            sdb.observer.off('draw', onDraw);
            sdb.observer.off('drawEnd', onEnd);

            const recording = {
                v: RECORDING_VERSION,
                w: CANVAS_W,
                h: CANVAS_H,
                events: eventsRef.current,
            };
            fetch('/dancelestrations/save-data', {
                method: 'post',
                credentials: 'include',
                body: JSON.stringify({dataType: saveDataType, value: JSON.stringify(recording)})
            }).catch(() => {});
            sdb.clear();
            sdb.destroy();
        };
    }, [word, existing, saveDataType]);

    const recordEvent = (type) => {
        if (startTimeRef.current === null) startTimeRef.current = performance.now();
        eventsRef.current.push({
            t: type,
            ts: Math.round(performance.now() - startTimeRef.current),
        });
    };

    const handleUndo = () => {
        if (!sdb) return;
        recordEvent('undo');
        sdb.undo();
    };

    const handleClear = () => {
        if (!sdb) return;
        recordEvent('clear');
        sdb.clear();
    };

    const updateSelectedColor = (color) => {
        setSelectedColor(color);
        if (sdb) sdb.setLineColor(color);
    };

    const ghostBtn = {
        flex: 1,
        padding: '12px 0',
        fontSize: 18,
        fontWeight: 700,
        color: 'black',
        backgroundColor: '#ffffff',
        border: '3px solid black',
        borderRadius: 6,
        cursor: 'pointer',
        boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
        fontFamily: 'inherit',
    };

    const smearMask = {
        WebkitMaskImage: 'url(/dancelestrations-assets/smear.png)',
        maskImage: 'url(/dancelestrations-assets/smear.png)',
        WebkitMaskSize: '100% 100%',
        maskSize: '100% 100%',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
    };

    return (
        <div style={{
            alignSelf: 'flex-start',
            minHeight: '100dvh',
            boxSizing: 'border-box',
            padding: 'calc(16px + env(safe-area-inset-top)) 16px calc(24px + env(safe-area-inset-bottom))',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 12,
            color: 'white',
        }}>
            <DancelestrationsBackdrop />
            <CountdownBadge roundEnd={roundEnd} clockDrift={clockDrift}/>
            <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 80}}>
                <div style={{
                    textAlign: 'center',
                    padding: '10px 24px 14px',
                    border: '3px solid black',
                    borderRadius: 6,
                    backgroundColor: '#ffffff',
                    color: 'black',
                    lineHeight: 1.15,
                    boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
                }}>
                    <div style={{
                        fontSize: 14,
                        fontWeight: 700,
                        letterSpacing: 3,
                        textTransform: 'uppercase',
                        opacity: 0.6,
                        marginBottom: 4,
                    }}>
                        Draw
                    </div>
                    <div style={{fontSize: 36, fontWeight: 700}}>
                        {word || 'Anything'}
                    </div>
                </div>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <canvas
                id="drawing"
                width="500"
                height="500"
                style={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    height: 'auto',
                    backgroundColor: '#0a0a0a',
                    border: '3px solid black',
                    borderRadius: 6,
                    touchAction: 'none',
                    boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
                }}
            />

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 4,
                alignItems: 'center',
                padding: '10px 12px',
                backgroundColor: '#232345',
                border: '3px solid black',
                borderRadius: 6,
                boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
            }}>
                {COLORS.map((color, i) => {
                    const isSelected = selectedColor === color;
                    const rotation = (i * 13) % 12 - 6;
                    return (
                        <div
                            key={color}
                            onClick={() => updateSelectedColor(color)}
                            aria-label={`color-${color}`}
                            style={{
                                flex: 1,
                                aspectRatio: '1 / 1',
                                position: 'relative',
                                cursor: 'pointer',
                                transform: isSelected ? 'translateY(-8px)' : 'translateY(0)',
                                transition: 'transform 120ms',
                            }}
                        >
                            {isSelected && (
                                <div style={{
                                    position: 'absolute',
                                    inset: '-6%',
                                    borderRadius: '50%',
                                    backgroundColor: 'black',
                                    border: '2px solid white',
                                    boxShadow: '3px 3px 0 rgba(0,0,0,0.7)',
                                }}/>
                            )}
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                backgroundColor: color,
                                transform: `rotate(${rotation}deg) scale(${isSelected ? 1.05 : 0.9})`,
                                filter: isSelected ? 'none' : 'drop-shadow(2px 2px 0 rgba(0,0,0,0.7))',
                                transition: 'transform 120ms',
                                ...smearMask,
                            }}/>
                        </div>
                    );
                })}
            </div>

            <div style={{display: 'flex', gap: 12}}>
                <button onClick={handleUndo} style={ghostBtn}>↶ Undo</button>
                <button onClick={handleClear} style={ghostBtn}>✕ Clear</button>
            </div>
            </div>
        </div>
    );
};

export const ShowDancelestrations = ({isControl, timestamp, clockDrift}) => {
    if(isControl) return <ShowDancelestrationsControl timestamp={timestamp} clockDrift={clockDrift} />;
    return <div style={{
        minHeight: '100dvh',
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }}>
        <img
            src="/dancelestrations-assets/title.jpg"
            alt="Dancelestrations"
            style={{width: '100%', height: 'auto', display: 'block'}}
        />
    </div>;
}

// Per-section playback budget. Each drawing replay scales to fit this; the
// gif also plays for this duration so the three sections feel balanced.
const PLAYBACK_TARGET_MS = 2000;
const GIF_PLAYBACK_MS = 3000;
const REVEAL_FADE_MS = 400;
const SECTION_PAUSE_MS = 1000;
const END_BEAT_MS = 6000;

const playbackCardStyle = {
    border: '3px solid black',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    color: 'black',
    boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
    padding: '14px 20px',
    width: 460,
    minHeight: 150,
    boxSizing: 'border-box',
    fontSize: 32,
    fontWeight: 500,
    lineHeight: 1.25,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
};

const mediaFrameStyle = {
    width: 500,
    height: 500,
    backgroundColor: '#0a0a0a',
    border: '3px solid black',
    borderRadius: 6,
    boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const fadeStyle = (revealed) => ({
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: revealed ? 1 : 0,
    transform: revealed ? 'translateY(0)' : 'translateY(12px)',
    transition: `opacity ${REVEAL_FADE_MS}ms ease-out, transform ${REVEAL_FADE_MS}ms ease-out`,
});

// Every main screen plays the same pair at the same moment: the playback runs on the show's clock, from when the
// engine brought the show to this scene (a screen that joins late lands mid-pair). Each pair is a fixed timeline;
// a drawing replays inside its slot (compressed when joined partway, finished off when its slot ends).
const PHASES = (() => {
    let t = 0;
    const at = (ms) => { const start = t; t += ms; return start; };
    const p = {col1: at(REVEAL_FADE_MS), draw1: at(PLAYBACK_TARGET_MS), pause1: at(SECTION_PAUSE_MS), col2: at(GIF_PLAYBACK_MS),
        pause2: at(SECTION_PAUSE_MS), col3: at(REVEAL_FADE_MS), draw2: at(PLAYBACK_TARGET_MS), end: at(END_BEAT_MS)};
    p.total = t;
    return p;
})();

const ShowDancelestrationsControl = ({timestamp, clockDrift = 0}) => {

    const [index, setIndex] = useState(0);
    const [count, setCount] = useState();
    const [dancelestration, setDancelestration] = useState();
    const [revealed, setRevealed] = useState([false, false, false]);

    const canvas1Ref = useRef(null);
    const canvas2Ref = useRef(null);
    const startRef = useRef(null);
    if (startRef.current == null) startRef.current = drawnScene() == null && timestamp ? timestamp : Date.now() + clockDrift;
    const elapsed = () => Date.now() + clockDrift - startRef.current;

    useEffect(() => {
        fetch('/dancelestrations/get-dancelestrations-count')
            .then(response => response.json())
            .then((json) => {
                setCount(Math.min(json.count, 12));
            });
    }, []);

    // Which pair the show's clock is on.
    useEffect(() => {
        const t = setInterval(() => setIndex(Math.max(0, Math.floor(elapsed() / PHASES.total))), 100);
        setIndex(Math.max(0, Math.floor(elapsed() / PHASES.total)));
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (count === undefined) return;
        if (index >= count) return;
        setDancelestration(null);
        setRevealed([false, false, false]);
        fetch('/dancelestrations/get-dancelestration?' + new URLSearchParams({index}))
            .then(response => response.json())
            .then(setDancelestration);
    }, [index, count]);

    // Reveal the columns and replay the drawings as the pair's timeline reaches them.
    useEffect(() => {
        if (!dancelestration) return;
        const pairStart = index * PHASES.total;
        const drawings = [
            {ref: canvas1Ref, rec: parseRecording(dancelestration.initial_drawing), from: PHASES.draw1, state: null},
            {ref: canvas2Ref, rec: parseRecording(dancelestration.dance_guess_drawing), from: PHASES.draw2, state: null},
        ];
        let shown = '';
        const step = () => {
            const t = elapsed() - pairStart;
            const r = [t >= PHASES.col1, t >= PHASES.col2, t >= PHASES.col3];
            if (r.join() !== shown) { shown = r.join(); setRevealed(r); }
            drawings.forEach(d => {
                if (!d.rec || !d.ref.current || d.state === 'done') return;
                const left = d.from + PLAYBACK_TARGET_MS - t;
                if (t < d.from) return;
                if (left <= 250) {          // its slot is over (or nearly): the finished drawing
                    if (d.state && d.state.cancel) d.state.cancel();
                    renderStrokes(d.ref.current, d.rec, {animated: false});
                    d.state = 'done';
                } else if (!d.state) {
                    d.state = renderStrokes(d.ref.current, d.rec, {animated: true, targetMs: left});
                }
            });
        };
        const timer = setInterval(step, 50);
        requestAnimationFrame(step);
        return () => {
            clearInterval(timer);
            drawings.forEach(d => { if (d.state && d.state.cancel) d.state.cancel(); });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dancelestration]);

    if (count === undefined) return null;
    if (index >= count) return null;
    if (!dancelestration) return <div style={{
        alignSelf: 'flex-start',
        width: '1600px',
        height: '900px',
        backgroundColor: '#c89458',
        position: 'relative',
        overflow: 'hidden',
    }}>
        <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/dancelestrations-assets/backdrop-landscape.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.4,
        }}/>
    </div>;

    const getDancingDescription = () => {
        if (dancelestration.initial_drawing_guess && dancelestration.dancing_gif_url) {
            return <>{dancelestration.dancer.name} danced<br/>{dancelestration.initial_drawing_guesser.name}'s guess:<br/><strong>{dancelestration.initial_drawing_guess}</strong></>;
        } else if (dancelestration.initial_drawing_guess && !dancelestration.dancing_gif_url) {
            return <>{dancelestration.initial_drawing_guesser.name} failed to capture<br/>{dancelestration.dancer.name} dancing their guess:<br/><strong>{dancelestration.initial_drawing_guess}</strong></>;
        } else if (!dancelestration.initial_drawing_guess && dancelestration.dancing_gif_url) {
            return <>{dancelestration.initial_drawing_guesser.name} didn't guess, but<br/>{dancelestration.dancer.name} danced anyway!</>;
        } else {
            return <>{dancelestration.initial_drawing_guesser.name} was negligent.</>;
        }
    };

    const noAnimationFallback = (
        <div style={{color: 'white', textAlign: 'center', fontSize: 28, padding: 16}}>
            No<br/>Dance<br/>:(
        </div>
    );

    const columnInnerStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
    };

    const cardSlotStyle = {
        minHeight: 150,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
    };

    const mediaSlotStyle = {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    };

    return <div style={{
        position: 'relative',
        alignSelf: 'flex-start',
        width: '1600px',
        height: '900px',
        boxSizing: 'border-box',
        padding: '40px 20px',
        gap: 30,
        backgroundColor: '#c89458',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'stretch',
        color: 'white',
        overflow: 'hidden',
    }}>
        <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/dancelestrations-assets/backdrop-landscape.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.4,
            zIndex: 0,
        }}/>

        <div style={{...fadeStyle(revealed[0]), ...columnInnerStyle}}>
            <div style={cardSlotStyle}>
                <div style={playbackCardStyle}>
                    {dancelestration.initial_drawer.name} drew:<br/>
                    <strong style={{fontSize: 42}}>{dancelestration.initial_word}</strong>
                </div>
            </div>
            <div style={mediaSlotStyle}>
                <div style={mediaFrameStyle}>
                    <canvas
                        ref={canvas1Ref}
                        id="drawing-one"
                        width={500}
                        height={500}
                        style={{width: '100%', height: '100%', display: 'block'}}
                    />
                </div>
            </div>
        </div>

        <div style={{...fadeStyle(revealed[1]), ...columnInnerStyle}}>
            <div style={cardSlotStyle}>
                <div style={playbackCardStyle}>
                    {getDancingDescription()}
                </div>
            </div>
            <div style={mediaSlotStyle}>
                {dancelestration.dancing_gif_url ? (
                    <img
                        src={dancelestration.dancing_gif_url}
                        alt="dance"
                        style={{
                            height: '100%',
                            maxHeight: 600,
                            maxWidth: '100%',
                            objectFit: 'contain',
                            backgroundColor: '#0a0a0a',
                            border: '3px solid black',
                            borderRadius: 6,
                            boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
                            display: 'block',
                        }}
                    />
                ) : (
                    <div style={mediaFrameStyle}>{noAnimationFallback}</div>
                )}
            </div>
        </div>

        <div style={{...fadeStyle(revealed[2]), ...columnInnerStyle}}>
            <div style={cardSlotStyle}>
                <div style={playbackCardStyle}>
                    {dancelestration.dance_guess ? (
                        <>{dancelestration.dance_guess_drawer.name} drew<br/>{dancelestration.dance_guesser.name}'s guess:<br/><strong style={{fontSize: 42}}>{dancelestration.dance_guess}</strong></>
                    ) : (
                        <>{dancelestration.dance_guesser.name} didn't guess!<br/>{dancelestration.dance_guess_drawer.name} drew anyway.</>
                    )}
                </div>
            </div>
            <div style={mediaSlotStyle}>
                <div style={mediaFrameStyle}>
                    <canvas
                        ref={canvas2Ref}
                        id="drawing-two"
                        width={500}
                        height={500}
                        style={{width: '100%', height: '100%', display: 'block'}}
                    />
                </div>
            </div>
        </div>
    </div>;
}